import type { QuizPlayer } from 'types/quiz-player';
import type {
  CaptainComparisonResult,
  ReadinessTeamMember
} from 'types/readiness';

/**
 * Resolves a squad member to its player by id. The AI's captain comes from the
 * team-rating derived block (an id), so the funnel maps it back to the squad
 * for display rather than projecting points client-side.
 */
const findPlayerById = (
  team: ReadinessTeamMember[],
  id: string | null
): QuizPlayer | null => {
  if (!id) return null;
  return team.find((member) => member.player?.id === id)?.player ?? null;
};

/**
 * Builds the captain comparison from the server's derived block: the AI captain
 * id and the projected-points delta of the AI captain over the user's captain.
 * No per-player predictions are read on the client.
 */
export const buildCaptainComparison = (
  team: ReadinessTeamMember[],
  userCaptain: QuizPlayer,
  aiCaptainId: string | null,
  captainPointsDelta: number | null
): CaptainComparisonResult => {
  const aiCaptain = findPlayerById(team, aiCaptainId) ?? userCaptain;

  return {
    userCaptain,
    aiCaptain,
    pointsDelta: captainPointsDelta,
    // Match against the resolved AI captain: when the server gave no id,
    // aiCaptain falls back to the user's own captain, which reads as agreement
    // rather than a spurious mismatch.
    isUserMatchingAi: userCaptain.id === aiCaptain.id
  };
};
